import React, { useEffect, useState } from "react";
import api, { downloadEsgReport, downloadAssetReport } from "../../api";
import { toast } from "sonner";
import { Leaf, FileDown, Pencil, Check, X, AlertTriangle } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, LabelList } from "recharts";

const CURRENT_YEAR = new Date().getFullYear();
const YEARS = Array.from({ length: 6 }, (_, i) => CURRENT_YEAR - i);

const fmtKm = (km) => (km == null || km === "" ? null : Number(km).toLocaleString("it-IT"));
const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString("it-IT"); } catch { return "—"; } };

export const EsgTab = () => {
  const [year, setYear] = useState(CURRENT_YEAR);
  const [data, setData] = useState({ rows: [], total_km: 0, count: 0 });
  const [series, setSeries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editVal, setEditVal] = useState("");

  const load = (y = year) => {
    setLoading(true);
    api.get(`/esg/summary?year=${y}`).then((r) => setData(r.data || { rows: [], total_km: 0, count: 0 }))
      .catch(() => toast.error("Errore nel caricamento dati ESG"))
      .finally(() => setLoading(false));
  };
  const loadSeries = () => {
    api.get(`/esg/yearly?years=5`).then((r) => setSeries(r.data?.series || [])).catch(() => {});
  };
  useEffect(() => { load(year); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [year]);
  useEffect(() => { loadSeries(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const startEdit = (row) => { setEditId(row.benefit_id); setEditVal(row.km != null ? String(row.km) : ""); };
  const cancelEdit = () => { setEditId(null); setEditVal(""); };
  const saveEdit = async (row) => {
    const val = Number(editVal);
    if (editVal === "" || Number.isNaN(val) || val < 0) { toast.error("Valore km non valido"); return; }
    try {
      await api.put(`/benefits/${row.benefit_id}/km`, { km: val, year });
      toast.success("Chilometri aggiornati");
      cancelEdit(); load(year); loadSeries();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const exportPdf = async () => {
    try { await downloadEsgReport(year, `esg_km_auto_${year}.pdf`); toast.success("Report ESG scaricato"); }
    catch { toast.error("Errore durante il download"); }
  };

  const exportAssetsPdf = async () => {
    try { await downloadAssetReport("all", "report_beni_aziendali.pdf", true); toast.success("Report beni aziendali scaricato"); }
    catch { toast.error("Errore durante il download"); }
  };

  const missing = data.rows.filter((r) => r.needs_update).length;

  return (
    <div data-testid="esg-tab">
      <div className="bg-gradient-to-r from-[#0f5132] to-[#157347] rounded-lg p-5 mb-6 text-white">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3">
            <span className="mt-0.5"><Leaf size={22} /></span>
            <div>
              <h2 className="text-[18px] font-black">Dati per la sostenibilità (ESG)</h2>
              <p className="text-[13px] text-white/80 mt-0.5 max-w-xl">Chilometri percorsi con le auto aziendali dai dipendenti e collaboratori assegnatari. Il dato va aggiornato al 31 dicembre di ogni anno.</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap justify-end">
            <button onClick={exportAssetsPdf} data-testid="esg-assets-report-btn" className="flex items-center gap-2 bg-white/15 hover:bg-white/25 border border-white/30 text-white text-[13px] font-bold px-3 py-1.5 rounded-sm transition-colors"><FileDown size={15} /> Report beni (PDF)</button>
            <label className="text-[12px] font-semibold text-white/80">Anno</label>
            <select value={year} onChange={(e) => setYear(Number(e.target.value))} data-testid="esg-year-select" className="bg-white/15 border border-white/30 rounded-sm px-3 py-1.5 text-[13px] font-bold text-white outline-none">
              {YEARS.map((y) => <option key={y} value={y} className="text-black">{y}</option>)}
            </select>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="bg-white border border-gray-200 rounded-lg p-5" data-testid="esg-total-km">
          <div className="text-[12px] font-semibold text-gray-500 uppercase tracking-wide">Totale km percorsi ({year})</div>
          <div className="text-[30px] font-black text-[#157347] mt-1">{fmtKm(data.total_km) || 0} <span className="text-[16px] font-bold text-gray-400">km</span></div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-5">
          <div className="text-[12px] font-semibold text-gray-500 uppercase tracking-wide">Assegnatari</div>
          <div className="text-[30px] font-black text-[#16345c] mt-1">{data.count}</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-5">
          <div className="text-[12px] font-semibold text-gray-500 uppercase tracking-wide">Da aggiornare</div>
          <div className={`text-[30px] font-black mt-1 ${missing > 0 ? "text-amber-600" : "text-gray-300"}`}>{missing}</div>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-5 mb-6" data-testid="esg-yearly-chart">
        <h3 className="text-[15px] font-bold text-[#16345c] mb-4">Andamento km totali per anno</h3>
        {series.some((s) => s.total_km > 0) ? (
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={series} margin={{ top: 20, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f6" vertical={false} />
              <XAxis dataKey="year" tick={{ fontSize: 12, fill: "#64748b" }} axisLine={{ stroke: "#e2e8f0" }} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} tickFormatter={(v) => v.toLocaleString("it-IT")} width={64} />
              <Tooltip
                formatter={(v) => [`${Number(v).toLocaleString("it-IT")} km`, "Totale"]}
                labelFormatter={(l) => `Anno ${l}`}
                contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 13 }}
              />
              <Bar dataKey="total_km" fill="#157347" radius={[6, 6, 0, 0]} maxBarSize={64}>
                <LabelList dataKey="total_km" position="top" formatter={(v) => (v > 0 ? Number(v).toLocaleString("it-IT") : "")} style={{ fontSize: 11, fill: "#0f5132", fontWeight: 700 }} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-[200px] flex items-center justify-center text-gray-400 text-[13px]" data-testid="esg-yearly-empty">Nessun dato km disponibile per gli ultimi anni.</div>
        )}
      </div>

      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <h3 className="text-[15px] font-bold text-[#16345c]">Chilometri per assegnatario</h3>
        <button onClick={exportPdf} data-testid="esg-report-pdf-btn" className="flex items-center gap-2 bg-[#16345c] hover:bg-[#0d1b2f] text-white text-[13px] font-bold px-4 py-2.5 rounded-sm transition-colors"><FileDown size={16} /> Report ESG (PDF)</button>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg overflow-x-auto">
        <table className="w-full min-w-[640px]">
          <thead>
            <tr className="bg-[#16345c] text-white text-[12px] uppercase tracking-wide">
              <th className="text-left px-4 py-3 font-bold">Assegnatario</th>
              <th className="text-left px-4 py-3 font-bold">Auto</th>
              <th className="text-left px-4 py-3 font-bold">Targa</th>
              <th className="text-right px-4 py-3 font-bold">Km {year}</th>
              <th className="text-left px-4 py-3 font-bold">Ultimo agg.</th>
              <th className="text-right px-4 py-3 font-bold">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {data.rows.map((r) => (
              <tr key={r.benefit_id} className="border-b border-gray-100 last:border-0 text-[13.5px]" data-testid={`esg-row-${r.benefit_id}`}>
                <td className="px-4 py-3 font-semibold text-[#16345c]">{r.employee_name || "—"}{r.returned && <span className="ml-2 text-[10px] font-bold uppercase bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded-full">riconsegnata</span>}</td>
                <td className="px-4 py-3 text-gray-600">{r.car_model || "—"}</td>
                <td className="px-4 py-3 text-gray-600">{r.car_plate || "—"}</td>
                <td className="px-4 py-3 text-right">
                  {editId === r.benefit_id ? (
                    <input type="number" min="0" value={editVal} onChange={(e) => setEditVal(e.target.value)} data-testid={`esg-edit-input-${r.benefit_id}`} className="w-28 border border-gray-300 rounded-sm px-2 py-1 text-[13px] text-right outline-none focus:border-[#157347]" autoFocus />
                  ) : r.km != null ? (
                    <span className="font-bold text-[#157347]">{fmtKm(r.km)} km</span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-[12px] font-semibold text-amber-600"><AlertTriangle size={13} /> da inserire</span>
                  )}
                </td>
                <td className="px-4 py-3 text-gray-500 text-[12px]">{r.updated_at ? fmtDate(r.updated_at) : "—"}</td>
                <td className="px-4 py-3 text-right">
                  {editId === r.benefit_id ? (
                    <div className="inline-flex items-center gap-1">
                      <button onClick={() => saveEdit(r)} data-testid={`esg-save-${r.benefit_id}`} title="Salva" className="w-7 h-7 inline-flex items-center justify-center rounded-sm text-[#157347] hover:bg-green-50"><Check size={15} /></button>
                      <button onClick={cancelEdit} title="Annulla" className="w-7 h-7 inline-flex items-center justify-center rounded-sm text-gray-400 hover:bg-gray-100"><X size={15} /></button>
                    </div>
                  ) : (
                    <button onClick={() => startEdit(r)} data-testid={`esg-edit-${r.benefit_id}`} title="Modifica km" className="w-7 h-7 inline-flex items-center justify-center rounded-sm text-[#16345c] hover:bg-blue-50"><Pencil size={14} /></button>
                  )}
                </td>
              </tr>
            ))}
            {!loading && data.rows.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-gray-400 text-[13px]">Nessuna auto aziendale assegnata.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
